import React from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { getLockViewForFeature } from "@/utils/FeaturePermissions";

interface FeatureGuardProps {
  featureKey: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  hideIfNoAccess?: boolean;
}

/**
 * FeatureGuard component that shows lock views for premium features
 * based on user permissions
 */
export const FeatureGuard: React.FC<FeatureGuardProps> = ({
  featureKey,
  children,
  fallback,
  hideIfNoAccess = false
}) => {
  const {
    features: userFeatures,
    modules: userModules,
    isLoading
  } = usePermissions();

  // Show loading state while permissions are being fetched
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Checking permissions...</span>
      </div>
    );
  }

  // Get the lock view component for this feature
  const LockViewComponent = getLockViewForFeature(
    featureKey,
    userFeatures,
    userModules
  );

  // If user doesn't have access
  if (LockViewComponent) {
    // If hideIfNoAccess is true, return null (hide completely)
    if (hideIfNoAccess) {
      return null;
    }
    // Otherwise show lock view or fallback
    return fallback || <LockViewComponent />;
  }

  // User has access, show the actual content
  return <>{children}</>;
};

export default FeatureGuard;
